import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

// ─── Retry Configuration ────────────────────────────────────────────────────────
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

/** Custom config property to track retry count (avoids __retryCount hacks). */
interface RetryConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
}

/** Returns true for errors that are worth retrying (transient network issues). */
function isRetryable(error: AxiosError): boolean {
  // Only retry network errors (no response) and 502/503/504 gateway errors
  if (!error.response) return true;
  const { status, data } = error.response;
  // A 503 with a JSON detail body is a deliberate "service not available" — don't retry
  if (status === 503 && data && typeof data === "object" && "detail" in data) {
    return false;
  }
  return [502, 503, 504].includes(status);
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Send HttpOnly cookies with every request
});

// ─── Request Interceptor: Attach CSRF Token ─────────────────────────────────
// Auth is handled via HttpOnly cookies (withCredentials: true).
// No Bearer tokens in localStorage — cookies are the sole auth transport.

apiClient.interceptors.request.use(
  (config) => {
    // CSRF token from non-HttpOnly cookie (double-submit pattern)
    // Use decodeURIComponent for proper handling of encoded characters
    const csrfMatch = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
    if (csrfMatch?.[1]) {
      try {
        config.headers["X-CSRF-Token"] = decodeURIComponent(csrfMatch[1]);
      } catch {
        config.headers["X-CSRF-Token"] = csrfMatch[1];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ─── Response Interceptor: Retry + Handle errors + Sentry ───────────────────────

apiClient.interceptors.response.use(
  (response) => {
    // Capture the request ID from backend for Sentry correlation
    const requestId = response.headers["x-request-id"];
    if (requestId) {
      Sentry.setTag("backend_request_id", requestId);
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;

    // ── Retry transient failures ───────────────────────────────────────────
    if (config && isRetryable(error)) {
      const retryCount = config._retryCount ?? 0;
      if (retryCount < MAX_RETRIES) {
        config._retryCount = retryCount + 1;
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (retryCount + 1)));
        return apiClient(config);
      }
    }

    // ── Error reporting & handling (after retries exhausted) ────────────────
    if (error.response) {
      const { status, data, headers } = error.response;
      const requestId = (headers as Record<string, string>)?.["x-request-id"];

      // Capture to Sentry with context
      Sentry.withScope((scope) => {
        scope.setTag("http_status", status.toString());
        if (requestId) {
          scope.setTag("backend_request_id", requestId);
        }
        scope.setContext("api_error", {
          url: error.config?.url,
          method: error.config?.method,
          status,
          response_data: typeof data === "object" ? data : { raw: String(data) },
        });

        // Only capture 5xx errors as exceptions (4xx are expected)
        if (status >= 500) {
          Sentry.captureException(
            new Error(`API ${status}: ${error.config?.method?.toUpperCase()} ${error.config?.url}`),
          );
        }
      });

      // 401 handling: auto-logout for expired sessions, but NOT for login failures
      if (status === 401) {
        const isLoginRequest = error.config?.url?.endsWith("/auth/login");
        if (!isLoginRequest) {
          // Dynamic import to avoid circular dependency
          import("../stores/authStore").then(({ useAuthStore }) => {
            useAuthStore.getState().logout();
          });
        }
      }
    } else if (error.request) {
      // Network error (no response received) — retries already exhausted
      Sentry.captureException(
        new Error(`Network error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`),
      );
    }

    return Promise.reject(error);
  }
);

export default apiClient;
